/**
 * Nexion NightWatch - Angular Frontend Check-In Simulator
 * (Multi-User Support: Two Users, Locations as [lon, lat])
 *
 * Simulates check-in requests for two users from the Angular frontend:
 * 1. Fetches the last packet sequence (GET /last-packet-sequence) for each user to start with.
 * 2. Iterates through defined lists of location coordinates formatted as [lon, lat].
 * 3. Generates random telemetry data (heart rate, battery, signal strength, etc.).
 * 4. Takes the next packet sequence from the check-in response (syncedPackets / sequence counter).
 * 5. Extracts dynamic check-in time from check-in response configurations per user.
 * 6. Sends check-in requests (POST /check-in) for both users.
 */

import { environment } from 'src/environments/environment';

/** GeoJSON-style location: [longitude, latitude], optional bearing as a third element. */
export type LocationPair = [number, number] | [number, number, number];

export interface SimulationUser {
  id: string;
  name: string;
  authToken: string;
  locations: LocationPair[];
  sequenceNumber: number;
  sequenceDate: string;
  dynamicIntervalMs: number;
}

/** Optional per-party location overrides for `runSimulation` ([lon, lat][]). */
export interface SimulationLocationOverride {
  survivor?: LocationPair[];
  perpetrator?: LocationPair[];
  onProgress?: (progress: SimulationProgress) => void;
}

export interface SimulationProgress {
  completedCheckIns: number;
  totalCheckIns: number;
  remainingCheckIns: number;
}

export interface GpsTelemetry {
  latitude: string;
  longitude: string;
}

export interface TelemetryPayload {
  gps: GpsTelemetry;
  heartRate: number;
  checkInTime: string;
  signalStrength: number;
  lteSignalStrength: number;
  batteryStatus: number;
  isEmergencyState: boolean;
  isCharging: boolean;
  isOnWrist: boolean;
  retryStatus: number;
  heartRateStatus: number;
  configTime: string;
  packetSequence: string;
  appVersionName: string;
}

export interface DeviceConfiguration {
  code: string;
  value: string | number | null;
  unit?: string;
}

export interface CheckInResponseData {
  syncedPackets?: string[];
  configurations?: DeviceConfiguration[];
  packetSequence?: string;
}

export interface ApiEnvelope<T = unknown> {
  data?: T;
  packetSequence?: string;
}

export interface HttpResponse<T = unknown> {
  statusCode: number;
  headers: Headers;
  data: T;
}

// Configuration
const API_BASE_URL = environment.apiUrl.replace(/\/web\/?$/, '/watch');
const DEVICE_APP_VERSION = '3.10.0-simulate';

function todaySequenceDate(): string {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

// Define Two Users with location coordinates as [lon, lat] arrays
const survivorDevToken = "20b8be717f881f3a1b1c2d16a5b24a7d763d1ca0076d63c3c9e348774b9c12701497a460e98ee423888a87f4fd65c41d28d1d770884d9010426f779ee4bc2491a9cff1968a802612ed6f375a4238ee2b20c1058d5847064fef14ec3f26d990e54728828f151410d1880680000edc2134c9cad29d8527bbad0b8e10b99854f81c";
const perpetratorDevToken = "20b8be717f881f3a1b1c2d16a5b24a7dc99302a696053aade406668a5949e3f03754ee21c7cb6a27a3883f70aff60530ab54d30a962e72edef6697149661feeb57583571ba9bf0023b7e720354dda5cc4fd4a27fdd9a8d4fe31b7368bcc0f2f729687931c33e6dfb64d04b960d7e8bd8e333d889ae46cdd9a737c1e40ebedbf9";
const survivorQaToken = "20b8be717f881f3a1b1c2d16a5b24a7dbb36199f20825c9d88d6fccda65ab82d129356b4e18a079672de5a2fbba155c1f68caba5bc7b06e5b3161445e4375c19c56069038fb584b55ee4b972a3481dc93158b99b3e3a41f551a63931c867a9ee779f741ba46de4c8f2ec500262d18a6c0f70475645cd59ee81d82edf45df0a34";
const perpetratorQaToken = "20b8be717f881f3a1b1c2d16a5b24a7d112e0c3c3bd4e9444817f6876f56df1109c50aaa04027e77558da5c87a2462e662fd50b069b617d51d6a9ca65f92b20101723a65def4cc3f2bab1740cdfee27dab20eb6e624c79f27c956b20138a649fa1c1e99a8473df4aa5f223be233c18b9d8e6b42a384891649ce92c535ad41151";
export const survivorDevId = "6a87e46dff75338bad4b62ab";
export const perpetratorDevId = "6a83f9e0d3f628e027b54ad4";
export const survivorQaId = "6aba3dddcfabd8e7ed7fbbfe";
export const perpetratorQaId = "6aba3e82cfabd8e7ed7fbc01";

const USERS: SimulationUser[] = [
  {
    id: getEnvironmentData() === 'Dev' ? survivorDevId : survivorQaId,
    name: 'Survivor',
    authToken: getEnvironmentData() === 'Dev' ? survivorDevToken : survivorQaToken,
    // Locations defined as [longitude, latitude] (GeoJSON format: [lon, lat])
    locations: [
        [
            85.3155263,
            27.7142794
          ],
          [
            85.3155138,
            27.71421
          ],
          [
            85.315495,
            27.7141434
          ],
          [
            85.3154887,
            27.7140796
          ],
          [
            85.3154824,
            27.7140158
          ],
          [
            85.3154793,
            27.7139797
          ],
          [
            85.315473,
            27.7139297
          ],
          [
            85.3154605,
            27.713877
          ],
          [
            85.3154605,
            27.7138271
          ],
          [
            85.3154385,
            27.7137632
          ],
          [
            85.3154229,
            27.7137022
          ],
          [
            85.3154166,
            27.713655
          ],
          [
            85.315404,
            27.7136245
          ],
          [
            85.3153946,
            27.7135579
          ],
          [
            85.3153821,
            27.7134996
          ],
          [
            85.3153476,
            27.7135051
          ],
          [
            85.3153602,
            27.7135606
          ],
          [
            85.3153633,
            27.7136078
          ],
          [
            85.3153633,
            27.7136411
          ],
          [
            85.3153257,
            27.7136633
          ],
          [
            85.3152504,
            27.7136689
          ],
          [
            85.3151783,
            27.71368
          ],
          [
            85.3151344,
            27.7136883
          ],
          [
            85.3150812,
            27.7137049
          ],
          [
            85.3150247,
            27.7137188
          ],
          [
            85.3149683,
            27.7137271
          ],
          [
            85.314915,
            27.7137327
          ],
          [
            85.3148492,
            27.7137438
          ],
          [
            85.3147739,
            27.7137604
          ],
          [
            85.3147112,
            27.7137771
          ],
          [
            85.3146517,
            27.7137882
          ],
          [
            85.314589,
            27.7137965
          ],
          [
            85.3145231,
            27.7138076
          ],
          [
            85.3144385,
            27.7138215
          ],
          [
            85.3143789,
            27.7138298
          ],
          [
            85.3143131,
            27.7138437
          ],
          [
            85.3142661,
            27.713852
          ],
          [
            85.3142159,
            27.7138576
          ],
          [
            85.3141909,
            27.7138881
          ],
          [
            85.3142285,
            27.7139103
          ],
          [
            85.3142285,
            27.7139103
          ]
    ],
    sequenceNumber: 1,
    sequenceDate: todaySequenceDate(),
    dynamicIntervalMs: 2000,
  },
  {
    id: getEnvironmentData() === 'Dev' ? perpetratorDevId : perpetratorQaId,
    name: 'Perpetrator',
    authToken: getEnvironmentData() === 'Dev' ? perpetratorDevToken : perpetratorQaToken,
    // Locations defined as [longitude, latitude] (GeoJSON format: [lon, lat])
    locations: [
          [
            85.3137047,
            27.6971306
          ],
          [
            85.3133516,
            27.6981067
          ],
          [
            85.3135975,
            27.7012831
          ],
          [
            85.313576,
            27.7012831
          ],
          [
            85.314985,
            27.7115275
          ],
          [
            85.3150298,
            27.7117867
          ],
          [
            85.3150641,
            27.7119742
          ],
          [
            85.3150983,
            27.7121437
          ],
          [
            85.3151263,
            27.7123132
          ],
          [
            85.3151571,
            27.7124826
          ],
          [
            85.3151932,
            27.7126852
          ],
          [
            85.3152247,
            27.7128685
          ],
          [
            85.3152582,
            27.7130521
          ],
          [
            85.315301,
            27.7132358
          ],
          [
            85.31533,
            27.7133998
          ],
          [
            85.3153584,
            27.7135196
          ],
          [
            85.3153849,
            27.7136367
          ],
          [
            85.3153927,
            27.713663
          ],
          [
            85.3155284,
            27.7136306
          ],
          [
            85.3156244,
            27.7136063
          ],
          [
            85.3157656,
            27.7135811
          ],
          [
            85.3158872,
            27.7135559
          ],
          [
            85.3160195,
            27.7135325
          ],
          [
            85.316198,
            27.7135031
          ],
          [
            85.3164221,
            27.7134573
          ],
          [
            85.3166503,
            27.7134073
          ],
          [
            85.3170296,
            27.7133412
          ],
          [
            85.3174657,
            27.7132671
          ],
          [
            85.3177928,
            27.7132094
          ],
          [
            85.3179899,
            27.7132306
          ],
          [
            85.3182145,
            27.7132782
          ],
          [
            85.3184773,
            27.7133443
          ],
          [
            85.3187997,
            27.7134183
          ],
          [
            85.3189877,
            27.7134737
          ],
          [
            85.3192204,
            27.7135213
          ]
    ],
    sequenceNumber: 1,
    sequenceDate: todaySequenceDate(),
    dynamicIntervalMs: 2000,
  },
];

let simulationAbort: AbortController | null = null;

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

async function delay(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  await new Promise<void>((resolve, reject) => {
    const onAbort = (): void => {
      clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function stopSimulation(): void {
  simulationAbort?.abort();
  simulationAbort = null;
}

export function isSimulationRunning(): boolean {
  return simulationAbort != null && !simulationAbort.signal.aborted;
}

/** Helper to make HTTP requests with user auth token */
async function makeRequest<T = unknown>(
  method: string,
  urlPath: string,
  data: unknown = null,
  authToken: string | null = null,
  signal?: AbortSignal
): Promise<HttpResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (authToken) {
    headers['nw-access-key'] = authToken;
  }

  const response = await fetch(`${API_BASE_URL}${urlPath}`, {
    method,
    headers,
    body: data != null ? JSON.stringify(data) : undefined,
    signal,
  });

  const text = await response.text();
  let parsed: T;
  try {
    parsed = (text ? JSON.parse(text) : null) as T;
  } catch {
    parsed = text as unknown as T;
  }

  return {
    statusCode: response.status,
    headers: response.headers,
    data: parsed,
  };
}

/** 1. Get last packet sequence for a user */
async function fetchLastPacketSequence(
  user: SimulationUser,
  signal: AbortSignal
): Promise<void> {
  console.log(`📡 [${user.name}] Fetching last packet sequence from GET /last-packet-sequence...`);
  try {
    const response = await makeRequest<ApiEnvelope<{ packetSequence?: string }>>(
      'GET',
      '/last-packet-sequence',
      null,
      user.authToken,
      signal
    );
    console.log(`📥 [${user.name}] Last Packet Response:`, response);

    const packetSeq =
      response.data?.data?.packetSequence ?? response.data?.packetSequence ?? null;

    if (packetSeq) {
      const parts = packetSeq.split('.');
      if (parts.length === 2) {
        user.sequenceDate = parts[0];
        user.sequenceNumber = parseInt(parts[1], 10) + 1;
        console.log(
          `✅ [${user.name}] Initialized sequence from server: Date = ${user.sequenceDate}, Next Number = ${user.sequenceNumber}`
        );
        return;
      }
    }
    console.log(
      `⚠️ [${user.name}] Could not parse packet sequence from server response, using default starting sequence.`
    );
  } catch (err: unknown) {
    if (isAbortError(err)) {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    console.warn(`⚠️ [${user.name}] Failed to fetch last packet sequence:`, message);
    console.log(
      `ℹ️ [${user.name}] Falling back to local sequence: Date = ${user.sequenceDate}, Number = ${user.sequenceNumber}`
    );
  }
}

/** Generate random telemetry data for a user using [lon, lat] location format */
function generateRandomTelemetry(
  user: SimulationUser,
  locationPair: LocationPair
): { telemetry: TelemetryPayload; sequenceStr: string } {
  const nowISO = new Date().toISOString();
  const formattedDate = todaySequenceDate();

  if (formattedDate !== user.sequenceDate) {
    user.sequenceDate = formattedDate;
    user.sequenceNumber = 1;
  }

  const sequenceStr = `${user.sequenceDate}.${String(user.sequenceNumber).padStart(4, '0')}`;

  // locationPair is [lon, lat]
  const longitude = locationPair[0];
  const latitude = locationPair[1];

  const telemetry: TelemetryPayload = {
    gps: {
      latitude: String(latitude),
      longitude: String(longitude),
    },
    heartRate: 0,
    checkInTime: nowISO,
    signalStrength: 0,
    lteSignalStrength: 0,
    batteryStatus: 15,
    isEmergencyState: false,
    isCharging: false,
    isOnWrist: false,
    retryStatus: 0,
    heartRateStatus: 0,
    configTime: nowISO,
    packetSequence: sequenceStr,
    appVersionName: DEVICE_APP_VERSION,
  };

  return { telemetry, sequenceStr };
}

/** Parse configurations from check-in response to extract dynamic check-in time for a user */
function parseCheckInConfigurations(
  user: SimulationUser,
  configurations: DeviceConfiguration[]
): void {
  for (const config of configurations) {
    if (config.code === 'CHECK_IN_TIME' && config.value != null) {
      const rawVal = parseFloat(String(config.value));
      if (!isNaN(rawVal) && rawVal > 0) {
        let intervalMs = rawVal;

        if (config.unit === 'Second' || config.unit === 'SECONDS') {
          intervalMs = rawVal * 1000;
        } else if (config.unit === 'Minute' || config.unit === 'MINUTES') {
          intervalMs = rawVal * 60 * 1000;
        } else if (config.unit === 'Hour' || config.unit === 'HOURS') {
          intervalMs = rawVal * 60 * 60 * 1000;
        }

        user.dynamicIntervalMs = intervalMs;
        console.log(
          `⏱️ [${user.name}] Dynamic check-in time configuration received: ${rawVal} (Set interval delay to ${user.dynamicIntervalMs} ms)`
        );
      }
    }
  }
}

/** 2. Simulate check-in for a user */
async function sendCheckIn(
  user: SimulationUser,
  telemetryData: TelemetryPayload,
  signal: AbortSignal
): Promise<unknown> {
  console.log(
    `🚀 [${user.name}] Sending Check-In packet [${telemetryData.packetSequence}] for location [lon: ${telemetryData.gps.longitude}, lat: ${telemetryData.gps.latitude}]...`
  );
  try {
    const response = await makeRequest<ApiEnvelope<CheckInResponseData>>(
      'POST',
      '/check-in',
      [telemetryData],
      user.authToken,
      signal
    );
    console.log(
      `📥 [${user.name}] Check-In Response status: ${response.statusCode}`,
      response.data
    );

    const resData = response.data?.data ?? (response.data as CheckInResponseData | undefined);
    if (resData) {
      if (
        resData.syncedPackets &&
        Array.isArray(resData.syncedPackets) &&
        resData.syncedPackets.length > 0
      ) {
        console.log(
          `🔄 [${user.name}] Synced packets acknowledged by server:`,
          resData.syncedPackets
        );
      }

      if (resData.configurations && Array.isArray(resData.configurations)) {
        parseCheckInConfigurations(user, resData.configurations);
      }
    }

    user.sequenceNumber++;
    return response.data;
  } catch (err: unknown) {
    if (isAbortError(err)) {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    console.error(`❌ [${user.name}] Check-In sync failed:`, message);
    throw err;
  }
}

/** Run simulation loop for an individual user */
async function simulateUser(
  user: SimulationUser,
  signal: AbortSignal,
  totalCheckIns: number,
  completedByUser: Map<string, number>,
  onProgress?: (progress: SimulationProgress) => void,
): Promise<void> {
  console.log('----------------------------------------');
  console.log(`👤 Starting simulation for ${user.name}`);
  console.log('----------------------------------------');

  try {
    // Step 1: Get last packet sequence for user
    await fetchLastPacketSequence(user, signal);

    // Step 2: Iterate through user's location coordinates [lon, lat] and send check-ins
    for (let i = 0; i < user.locations.length; i++) {
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const locPair = user.locations[i];
      const { telemetry } = generateRandomTelemetry(user, locPair);

      try {
        await sendCheckIn(user, telemetry, signal);
      } catch (err: unknown) {
        if (isAbortError(err)) {
          throw err;
        }
        console.log(`[${user.name}] Continuing simulation despite error...`);
      }

      completedByUser.set(user.name, i + 1);
      const completedCheckIns = Math.max(...completedByUser.values());
      onProgress?.({
        completedCheckIns,
        totalCheckIns,
        remainingCheckIns: Math.max(totalCheckIns - completedCheckIns, 0),
      });

      if (i < user.locations.length - 1) {
        console.log(
          `⏳ [${user.name}] Waiting ${user.dynamicIntervalMs} ms (dynamic check-in interval) before next check-in...\n`
        );
        await delay(user.dynamicIntervalMs, signal);
      }
    }

    console.log(`✨ [${user.name}] Simulation completed successfully!`);
  } catch (err: unknown) {
    if (isAbortError(err)) {
      console.log(`🛑 [${user.name}] Simulation stopped.`);
      return;
    }
    throw err;
  }
}

/** Main simulation runner for all users */
export async function runSimulation(
  overrides?: SimulationLocationOverride
): Promise<void> {
  stopSimulation();
  simulationAbort = new AbortController();
  const { signal } = simulationAbort;

  const users: SimulationUser[] = USERS.map((u) => {
    let locations = u.locations;
    if (u.name === 'Survivor' && overrides?.survivor?.length) {
      locations = overrides.survivor;
    } else if (u.name === 'Perpetrator' && overrides?.perpetrator?.length) {
      locations = overrides.perpetrator;
    }
    return {
      ...u,
      locations,
      sequenceNumber: 1,
      sequenceDate: todaySequenceDate(),
    };
  });
  const totalCheckIns = Math.max(...users.map((user) => user.locations.length), 0);
  const completedByUser = new Map<string, number>();
  overrides?.onProgress?.({
    completedCheckIns: 0,
    totalCheckIns,
    remainingCheckIns: totalCheckIns,
  });

  console.log('========================================');
  console.log('🤖 Nexion NightWatch Multi-User Simulator');
  console.log('========================================');
  console.log(`Target API Base URL: ${API_BASE_URL}`);
  console.log(`Simulating ${users.length} users with [lon, lat] locations concurrently...`);
  console.log('========================================\n');

  try {
    await Promise.all(
      users.map((user) =>
        simulateUser(user, signal, totalCheckIns, completedByUser, overrides?.onProgress),
      ),
    );

    if (!signal.aborted) {
      console.log('\n========================================');
      console.log('✨ All users simulation completed!');
      console.log('========================================');
    } else {
      console.log('\n========================================');
      console.log('🛑 Simulation stopped.');
      console.log('========================================');
    }
  } finally {
    if (simulationAbort?.signal === signal) {
      simulationAbort = null;
    }
  }
}

export function getDefaultSimulationLocations(): {
  survivor: LocationPair[];
  perpetrator: LocationPair[];
} {
  const survivor = USERS.find((u) => u.name === 'Survivor');
  const perpetrator = USERS.find((u) => u.name === 'Perpetrator');
  return {
    survivor: survivor ? [...survivor.locations] : [],
    perpetrator: perpetrator ? [...perpetrator.locations] : [],
  };
}

export function getSimulationUserIds(): string[] {
  return USERS.map((user) => user.id);
}

export function getEnvironmentData(): string {
  if (environment.apiUrl.includes('qa')) {
    return 'QA';
  }
  if (environment.apiUrl.includes('dev') || environment.apiUrl.includes('localhost')) {
    return 'Dev';
  }
  return 'Prod';
}
